import { HomeGreeting } from '../../ui/HomeGreeting';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { crmMyDay, salesSignals, type SalesSignal } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { usePageTab } from '../../shell/Shell';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { useQuery } from '../../useQuery';
import { SalesContext, SALES_TABS, type SalesCtx, type SalesTab } from './context';
import { useSalesParams } from './params';
import { ck, hov, sx } from './dc';
import { CrmTab } from './crm/CrmTab';
import { KitTab } from './kit/KitTab';
import { OutcomesTab } from './outcomes/OutcomesTab';
import { TargetingTab } from './targeting/TargetingTab';
import { MeetingPrep } from './crm/MeetingPrep';
import { BuildTagged } from '../../ui/buildLabels';

/** Poll Signals this often: the server has no push channel for the console yet. */
export const SIGNALS_POLL_MS = 60_000;
/** The ticker moves to the next signal this often (design _slTick, L20777). */
export const TICKER_MS = 4500;

const WRITE_ROLES = new Set(['SUPER_ADMIN', 'OPERATOR', 'SALES']);

/** "2 deals wait on you. Start with the oldest contact." (design crmWaitLine). */
export function waitWords(n: number): string {
  return n === 0 ? 'No deals are waiting on you.' : n + (n === 1 ? ' deal waits on you.' : ' deals wait on you.') + ' Start with the oldest contact.';
}

// design salesVals pill() (L20031)
const pill = (on: boolean) =>
  'display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:rgba(79,138,214,.15);color:#2F5C97;' : 'background:transparent;color:#54606C;');
const chip = (fg: string, bg: string) =>
  'font-size:14px;font-weight:700;letter-spacing:.07em;border-radius:5px;padding:2px 7px;flex:0 0 auto;white-space:nowrap;color:' + fg + ';background:' + bg + ';';

type Sev = 'act' | 'follow' | 'info';
const SEV: Record<Sev, [string, string, string]> = {
  act: ['ACT', '#B3261E', 'rgba(179,38,30,.12)'],
  follow: ['FOLLOW UP', '#8A5A00', 'rgba(165,86,26,.13)'],
  info: ['OPPORTUNITY', '#264C7E', 'rgba(47,92,151,.12)'],
};
const sevOf = (s: SalesSignal): Sev => (s.level === 'ACT' ? 'act' : s.level === 'OPPORTUNITY' ? 'info' : 'follow');

interface SigRow {
  sev: Sev;
  label: string;
  key: string | null;
  facilityId: string | null;
  agg: boolean;
  t: string;
  why: string;
  tag: string;
}

/**
 * The server's signals as the design's ticker rows (design L20760-20776): a headline and
 * a reason, one row per facility and severity (two rules on one facility are merged into
 * one row), ACT first, then follow-ups, then opportunities.
 */
export function signalRows(list: SalesSignal[]): SigRow[] {
  const sig: SigRow[] = list.map((s) => {
    const sev = sevOf(s);
    const agg = !s.facility_id && !s.deal_id;
    return {
      sev,
      label: s.level === 'CRM' ? 'CRM' : SEV[sev][0],
      key: s.facility_id ?? s.deal_id,
      facilityId: s.facility_id,
      agg,
      t: s.headline ?? (agg ? `${s.title}: ${s.detail}` : `${s.detail}: ${s.title}`),
      why: s.why ?? '',
      tag: s.modeled ? 'MODELED' : '',
    };
  });
  for (let i = sig.length - 1; i > 0; i--) {
    const x = sig[i];
    if (x.agg || !x.key) continue;
    const j = sig.findIndex((y, k) => k < i && !y.agg && y.key === x.key && y.sev === x.sev);
    if (j >= 0) {
      const y = sig[j];
      const name = y.t.includes(': ') ? y.t.slice(y.t.indexOf(': ') + 2) : '';
      y.t = y.t.replace(/: .*/, '') + ' · ' + x.t.replace(/: .*/, '') + ': ' + name;
      y.why = [y.why, x.why].filter(Boolean).join(' ');
      y.tag = y.tag || x.tag;
      sig.splice(i, 1);
    }
  }
  const ord: Record<Sev, number> = { act: 0, follow: 1, info: 2 };
  return sig.sort((a, b) => ord[a.sev] - ord[b.sev]);
}

/**
 * Sales (design isSales, T6157-7357; salesVals L20026). Four tabs: CRM, Sales Kit,
 * Outcomes and Targeting, with the Signals ticker in the header on CRM and Targeting.
 * Aithora staff only: Super Admin and Operator read and write everything, Sales reads
 * everything and writes what it owns, Partner reads (the server enforces all of it).
 */
export function Sales() {
  const { session, client } = useSession();
  const { role } = useConsoleRole();
  const roReason = useReadOnlyReason();
  const { params, setParam, setTab, openPrep } = useSalesParams();
  const tab: SalesTab = SALES_TABS.find((t) => t.toLowerCase() === (params.get('tab') ?? '').toLowerCase()) ?? 'CRM';
  usePageTab(tab);
  const [crmNarrow, setCrmNarrow] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigI, setSigI] = useState(0);

  // The meeting prep opens from a link too (?mpOpen=1 to pick, ?mpFor=<facility id>).
  const prepId = params.get('mpFor');
  const prepOpen = !!prepId || !!params.get('mpOpen');

  const isManager = role === 'SUPER_ADMIN' || role === 'OPERATOR';
  const canWrite = !!role && WRITE_ROLES.has(role) && roReason === null;
  const writeReason = roReason ?? (role === 'PARTNER' ? 'Partner access is read-only.' : null);

  const sigQ = useQuery((signal) => salesSignals(client, { query: { scope: 'mine', tz: Intl.DateTimeFormat().resolvedOptions().timeZone }, signal }), [client, session?.token], {
    pollMs: SIGNALS_POLL_MS,
  });
  const sig = useMemo(() => signalRows(sigQ.data?.signals ?? []), [sigQ.data]);
  useEffect(() => {
    if (sigOpen) return;
    const id = window.setInterval(() => setSigI((i) => i + 1), TICKER_MS);
    return () => window.clearInterval(id);
  }, [sigOpen]);

  const ctx: SalesCtx = useMemo(
    () => ({
      role,
      userId: session?.user.id ?? null,
      isManager,
      canWrite,
      writeReason,
      setTab,
      openPrep,
      openDeal: ({ dealId, facilityId }) => setTab('CRM', { crmSel: dealId ?? null, crmFac: facilityId ?? null }),
      params,
      setParam,
      crmNarrow,
      setCrmNarrow,
      reloadSignals: sigQ.reload,
    }),
    [role, session?.user.id, isManager, canWrite, writeReason, setTab, openPrep, params, setParam, crmNarrow, sigQ.reload],
  );

  // V9 "waits on you" line above the tabs (design crmWaitLine): the viewer's open deals from
  // their own day (GET /crm/my-day, scope mine), oldest contact first; Open the oldest opens it.
  const dayQ = useQuery((signal) => crmMyDay(client, { query: { scope: 'mine', tz: Intl.DateTimeFormat().resolvedOptions().timeZone }, signal }), [client, session?.token]);
  const waiting = (dayQ.data?.today ?? []).filter((d) => !d.snoozed_until);
  const oldest = waiting.slice().sort((a, b) => (a.last_contact_at ? Date.parse(a.last_contact_at) : 0) - (b.last_contact_at ? Date.parse(b.last_contact_at) : 0))[0] ?? null;
  const waitLine = dayQ.data ? waitWords(waiting.length) : null;

  const actN = sig.filter((x) => x.sev === 'act').length;
  const cur = sig.length ? sig[sigI % sig.length] : null;
  const go = (x: SigRow) => () => {
    setSigOpen(false);
    setTab('Targeting', x.facilityId ? { tgSel: x.facilityId } : {});
  };
  const sevStyle = (s: Sev) =>
    'font-size:14px;font-weight:800;letter-spacing:.08em;border-radius:4px;padding:2px 6px;white-space:nowrap;color:' + SEV[s][1] + ';background:' + SEV[s][2] + ';';
  const v = {
    slNotOutcomes: tab !== 'Outcomes' && tab !== 'Sales Kit',
    slSigHas: sig.length > 0,
    slSigCount: String(sig.length),
    slSigTicker: !crmNarrow,
    slSigBtnStyle:
      'flex:0 0 auto;display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 11px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#B3261E;border:1px solid rgba(179,38,30,.3);background:' +
      (sigOpen ? 'rgba(179,38,30,.1)' : 'transparent') +
      ';',
    slSigBadge:
      'display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:22px;padding:0 5px;box-sizing:border-box;border-radius:10px;background:#B3261E;color:#fff;font-size:14px;font-weight:800;' +
      (actN ? 'animation:pulse 2s ease-in-out infinite;' : ''),
    slSigHead:
      actN + ' to act on · ' + sig.filter((x) => x.sev === 'follow').length + ' follow-ups · ' + sig.filter((x) => x.sev === 'info').length + ' opportunities',
    slSeeOnboard: (role === 'SUPER_ADMIN' || role === 'OPERATOR') && tab === 'Sales Kit',
    slOnboardStyle:
      'display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 12px;border-radius:8px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;background:rgba(79,138,214,.13);color:#2F5C97;border:1px solid rgba(47,92,151,.3);',
  };

  return (
    <SalesContext.Provider value={ctx}>
      <div data-screen-label="Sales" data-testid="sales-page" style={sx('display:flex;flex-direction:column;gap:9px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
        {/* Sales is a salesperson's home (homeViewFor): it opens on their greeting (Andrew, 2026-10-19). */}
        {role === 'SALES' && <HomeGreeting />}
        {waitLine && (
          <div data-testid="sales-wait" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:12px 14px;margin-bottom:8px;border-radius:10px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
            <span style={sx('flex:1 1 300px;min-width:0;font-size:17px;font-weight:700;color:#22272E;line-height:1.4;')}>{waitLine}</span>
            {oldest && (
              <a
                href="#"
                data-testid="sales-wait-open"
                onClick={(e) => {
                  e.preventDefault();
                  ctx.openDeal({ dealId: oldest.deal_id, facilityId: oldest.facility_id ?? undefined });
                }}
                style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 20px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}
              >
                Open the oldest
              </a>
            )}
          </div>
        )}
        <div role="tablist" aria-label="Sales" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:7px;white-space:nowrap;height:40px;min-width:0;')}>
          {SALES_TABS.map((t) => (
            <Fragment key={t}>
              <a href="#" role="tab" aria-selected={tab === t} onClick={ck(() => setTab(t))} style={sx(pill(tab === t))}>
                <span>{t}</span>
              </a>
            </Fragment>
          ))}
          {v.slNotOutcomes && v.slSigHas && (
            <span style={sx('position:relative;flex:1000 1 0;min-width:118px;display:flex;align-items:center;gap:8px;margin-left:8px;overflow:hidden;')}>
              <a href="#" aria-expanded={sigOpen} onClick={ck(() => setSigOpen((o) => !o))} style={sx(v.slSigBtnStyle)}>
                &#9873; Signals{' '}
                <span style={sx(v.slSigBadge)}>
                  <span>{v.slSigCount}</span>
                </span>
              </a>
              {v.slSigTicker && cur && (
                <a href="#" onClick={ck(go(cur))} key={String(sigI)} style={sx('flex:1 1 auto;min-width:0;display:flex;align-items:center;gap:7px;text-decoration:none;color:#22272E;animation:riseIn .35s cubic-bezier(.22,.61,.36,1);')}>
                  <span style={sx(sevStyle(cur.sev))}>
                    <span>{cur.label}</span>
                  </span>
                  <span style={sx('font-size:16px;font-weight:600;min-width:0;overflow-wrap:break-word;')}>
                    <span>{cur.t}</span>
                  </span>
                </a>
              )}
              {sigOpen && (
                <span data-testid="signals-list" style={sx('position:fixed;left:50%;transform:translateX(-50%);top:120px;z-index:60;width:440px;max-width:calc(100vw - 32px);max-height:420px;overflow-y:auto;overflow-x:hidden;white-space:normal;box-sizing:border-box;display:flex;flex-direction:column;background:#FCFAF4;border-radius:10px;box-shadow:0 14px 40px rgba(34,39,46,.2);')}>
                  <span style={sx('display:flex;align-items:center;gap:8px;padding:10px 12px;')}>
                    <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Live signals</span>
                    <span style={sx('flex:1;')} />
                    <span style={sx('font-size:14px;color:#54606C;')}>
                      <span>{v.slSigHead}</span>
                    </span>
                  </span>
                  {sig.map((x, i) => (
                    <Fragment key={i}>
                      <a
                        href="#"
                        onClick={ck(go(x))}
                        style={sx(
                          'display:flex;flex-direction:column;gap:3px;padding:9px 12px;text-decoration:none;color:inherit;border-top:1px solid rgba(34,39,46,.08);animation:riseIn .25s cubic-bezier(.22,.61,.36,1) both;animation-delay:' +
                            Math.min(300, i * 30) +
                            'ms;',
                        )}
                        className={hov('background:rgba(79,138,214,.07);')}
                      >
                        <span style={sx('display:flex;align-items:center;gap:7px;')}>
                          <span style={sx(sevStyle(x.sev) + 'align-self:flex-start;')}>
                            <span>{x.label}</span>
                          </span>
                          <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;flex:1 1 auto;overflow-wrap:anywhere;')}>
                            <span>{x.t}</span>
                          </span>
                          {!!x.tag && (
                            <BuildTagged tag={x.tag}>
                            <span style={sx(chip('#264C7E', 'rgba(47,92,151,.12)'))}>
                              <span>{x.tag}</span>
                            </span>
                            </BuildTagged>
                          )}
                        </span>
                        <span style={sx('font-size:14px;color:#54606C;line-height:1.4;min-width:0;overflow-wrap:anywhere;')}>
                          <span>{x.why}</span>
                        </span>
                      </a>
                    </Fragment>
                  ))}
                </span>
              )}
            </span>
          )}
          <span style={sx('flex:1;')} />
        </div>
        <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:block;padding-right:3px;')}>
          {v.slSeeOnboard && (
            <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;margin:0 0 12px;')}>
              <a href="/help?q=sales%20onboarding" style={sx(v.slOnboardStyle)}>
                <span>New rep? Start the sales onboarding</span> &#8594;
              </a>{' '}
              <span style={sx('font-size:14px;color:#54606C;text-wrap:pretty;')}>
                <span>21 lessons, Aithora staff only, ends in a readiness packet your manager signs</span>
              </span>
            </div>
          )}
          {tab === 'Sales Kit' && <KitTab />}
          {tab === 'Targeting' && <TargetingTab />}
          {tab === 'Outcomes' && <OutcomesTab />}
          {tab === 'CRM' && <CrmTab />}
          {prepOpen && <MeetingPrep facilityId={prepId} onClose={() => { setParam('mpFor', null); setParam('mpOpen', null); }} onPick={(id) => ctx.openPrep(id)} />}
        </div>
      </div>
    </SalesContext.Provider>
  );
}
